/* ===============================
   QUICK VIEW MODAL
================================= */

class QuickViewModal extends HTMLElement {
  constructor() { 
    super();

    this.content = this.querySelector('.qv-content');
    this.overlay = this.querySelector('.qv-overlay');

    this.handleKeydown = this.handleKeydown.bind(this);
    this.lastFocusedElement = null;
  }

  connectedCallback() {

    this.addEventListener('click', (e) => {
      if (e.target.closest('.qv-close')) {
        this.close();
      }
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-view]');
      if (!btn) return;

      e.preventDefault();

      const handle = btn.dataset.productHandle;
      if (!handle) return;

      this.open(handle, btn);
    });
  }

  async open(handle, triggerBtn) {

    if (this.classList.contains('is-open')) return;
    if (triggerBtn.classList.contains('loading')) return;

    triggerBtn.classList.add('loading');

    this.lastFocusedElement = triggerBtn;

    try {
      const response = await fetch(`/products/${handle}`);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Prefer the main PDP section — the first `.product-section-container` on
      // some templates can be featured-product or another block.
      const innerWrapper =
        doc.querySelector('[data-section-type="main-product"] .product-section-container') ||
        doc.querySelector('.main-product .product-section-container') ||
        doc.querySelector('.product-section-container');

      setTimeout(() => {

        document.documentElement.classList.add('qv-open');
        this.classList.add('is-open');

        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('tabindex', '-1');

        this.focus();

        if (innerWrapper) {

          const productSectionBefore = innerWrapper.querySelector('product-section');

          if (productSectionBefore) {
            const originalSectionId = productSectionBefore.dataset.sectionId;
            const updatedSectionId = `quick-view-${originalSectionId}`;

            innerWrapper.innerHTML = innerWrapper.innerHTML.replaceAll(
              originalSectionId,
              updatedSectionId
            );

            // innerHTML replace destroys the old subtree; query again so datasets
            // apply to the node that is actually copied into the modal.
            const productSection = innerWrapper.querySelector('product-section');
            if (productSection) {
              productSection.dataset.originalSectionId = originalSectionId;
              productSection.dataset.updateUrl = 'false';
              // Use section rendering for variant fetches (reliable HTML, matches
              // non–main-product ProductSection behaviour).
              productSection.dataset.mainProduct = 'false';
            }
          }

          innerWrapper.querySelector('sticky-cart')?.remove();

          this.content.innerHTML = innerWrapper.innerHTML;

          this.initializeModalLogic();

          this.enableFocusAndTrap();

        } else {
          this.content.innerHTML = '<p>Product not found</p>';
        }

        triggerBtn.classList.remove('loading');

      }, 200);

    } catch (error) {
      triggerBtn.classList.remove('loading');
      console.error('Quick view fetch error:', error);
    }
  }

  initializeModalLogic() {
    const mainProduct = this.content.querySelector('main-product');
    if (!mainProduct) return;

    const initialVariantData = mainProduct.querySelector('[data-selected-variant]');
    if (initialVariantData) {
      const variant = JSON.parse(initialVariantData.textContent);
    }
  }


  getFocusableElements() {
    return Array.from(
      this.querySelectorAll('button, input:not([type="hidden"]), a, select, textarea')
    );
  }

  enableFocusAndTrap() {

    const elements = this.getFocusableElements();
    if (!elements.length) return;

    this.firstEl = elements[0];
    this.lastEl = elements[elements.length - 1];

    const closeBtn = this.querySelector('.qv-close');

    const setFocus = () => {
      const target = closeBtn || this.firstEl;
      if (!target) return;

      target.focus({ preventScroll: true });

      if (document.activeElement !== target) {
        setTimeout(() => {
          target.focus({ preventScroll: true });
        }, 50);
      }
    };

    setTimeout(setFocus, 0);

    this.addEventListener('keydown', this.handleKeydown);
  }

  handleKeydown(e) {
    if (!this.classList.contains('is-open')) return;

    // ESC closes modal
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    if (e.key !== 'Tab') return;

    if (e.shiftKey && document.activeElement === this.firstEl) {
      e.preventDefault();
      this.lastEl.focus();
    } else if (!e.shiftKey && document.activeElement === this.lastEl) {
      e.preventDefault();
      this.firstEl.focus();
    }
  }

  close() {
  if (this.classList.contains('closing')) return;

  this.classList.add('closing');

  this.removeEventListener('keydown', this.handleKeydown);

  if (this.lastFocusedElement && document.body.contains(this.lastFocusedElement)) {
    this.lastFocusedElement.focus({ preventScroll: true });
    this.lastFocusedElement = null;
  }

  setTimeout(() => {
    document.documentElement.classList.remove('qv-open');

    this.classList.remove('is-open', 'closing');

    this.content.innerHTML = '';

  }, 1000); 
}
}

if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', QuickViewModal);
}